import React, { useState, useEffect } from 'react';
import {
  TextField,
  Divider,
  Alert,
  Autocomplete,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { CallReceivedOutlined } from '@mui/icons-material';
import OrderFormLayout from '../../components/orders/OrderFormLayout';
import InboundItemsTable, {
  InboundOrderItem,
  AvailableItem,
} from '../../components/orders/InboundItemsTable';
import { api } from '../../services/api';
import { Partner, OrderDirection } from '../../types/api';
import { useNotification } from '../../contexts/NotificationContext';

const SupplierInboundOrderCreate: React.FC = () => {
  const { alert } = useNotification();

  // Form state
  const [holdInventory, setHoldInventory] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Partner | null>(null);
  const [remark, setRemark] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Suppliers state
  const [suppliers, setSuppliers] = useState<Partner[]>([]);

  // Items state
  const [availableItems, setAvailableItems] = useState<AvailableItem[]>([]);
  const [orderItems, setOrderItems] = useState<InboundOrderItem[]>([
    {
      item_id: 0,
      item_name: '',
      quantity: 0,
      note: '',
    },
  ]);

  // Fetch suppliers and items on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [suppliersRes, itemsRes] = await Promise.all([
          api.get('/api/partners', { params: { type: 'SUPPLIER' } }),
          api.get('/api/items'),
        ]);
        setSuppliers(suppliersRes.data.partners || []);
        setAvailableItems((itemsRes.data.items || []).map((item: any) => ({
          ...item,
          units_per_carton: item.metadata?.units_per_carton ?? 0,
        })));
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      }
    };
    fetchData();
  }, []);

  const validateForm = (): boolean => {
    if (!selectedSupplier) {
      setError('Please select a supplier');
      return false;
    }

    const validItems = orderItems.filter(
      (item) => item.item_id > 0 && item.quantity > 0
    );

    if (validItems.length === 0) {
      setError('Please add at least one item with a valid quantity');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setLoading(true);

    const payload = {
      direction: OrderDirection.INWARD,
      partner_id: selectedSupplier?.id,
      items: orderItems
        .filter((item) => item.item_id > 0)
        .map((item) => ({
          item_id: item.item_id,
          order_qty: item.quantity,
          note: item.note,
        })),
      metadata: {
        note: remark || 'Stock received from supplier',
      },
    };

    try {
      const endpoint = holdInventory ? '/api/orders/supplier' : '/api/orders/instant';
      const response = await api.post(endpoint, payload);
      const orderId = response.data?.id || response.data?.order?.id;
      // Reset form
      setSelectedSupplier(null);
      setRemark('');
      setOrderItems([{ item_id: 0, item_name: '', quantity: 0, note: '' }]);
      setHoldInventory(false);
      // Show success dialog
      await alert({
        title: 'Order Created',
        message: holdInventory
          ? `Order #${orderId || ''} created. Inventory is on hold — confirm when ready to make it live.`
          : `Order #${orderId || ''} created and inventory updated successfully!`,
        icon: 'success',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to create order');
      console.error('Error creating order:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OrderFormLayout
      title="Create Inward Order (Supplier)"
      icon={<CallReceivedOutlined sx={{ fontSize: 28 }} />}
      onSubmit={handleSubmit}
      submitLabel="Create Order"
      loadingLabel="Creating..."
      loading={loading}
      cancelPath="/suppliers/orders"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Supplier Selection */}
      <Autocomplete
        fullWidth
        options={suppliers}
        getOptionLabel={(option) => option.name}
        value={selectedSupplier}
        onChange={(_, newValue) => setSelectedSupplier(newValue)}
        filterOptions={(options, { inputValue }) => {
          const filterValue = inputValue.toLowerCase();
          return options.filter((option) =>
            option.name.toLowerCase().includes(filterValue)
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Supplier"
            placeholder="Type to search supplier..."
            required
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Typography variant="body1">{option.name}</Typography>
            </Box>
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 2 }} />

      {/* Items Table */}
      <InboundItemsTable
        items={orderItems}
        availableItems={availableItems}
        onItemsChange={setOrderItems}
      />

      <Divider sx={{ my: 2 }} />

      {/* Remark */}
      <TextField
        fullWidth
        label="Notes"
        multiline
        rows={2}
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
        placeholder="Add any notes about this order..."
      />

      {/* Hold inventory checkbox */}
      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <Checkbox
            checked={holdInventory}
            onChange={(e) => setHoldInventory(e.target.checked)}
            color="warning"
          />
        }
        label="Hold inventory — don't update stock until confirmed"
      />
    </OrderFormLayout>
  );
};

export default SupplierInboundOrderCreate;
